import { useEffect, useState } from 'react';
import { IConfig } from '../../../../Interfaces/IConfig';
import { ILegalTransactionRelationship } from '../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import {
  getEmptySignatureCertificationRelationshipData,
  ISignatureCertificationRelationshipData,
} from '../../../../Interfaces/ILegalTransactionRelationship/Relationships/SignatureCertification/ILegalTransactionRelationshipSignatureCertificationData';
import { DialogFrame } from '../../../core/DialogFrame';
import Send from '../../../generic/Send';
import { useLegalClientStore } from '../generic/legalClientStore';
import {
  createChildren,
  createStepperArray,
  ISavedRelationshipDialogData,
  TransitionStatus,
  updateOrCreateRelationship,
} from '../generic/utilities';
import { RestoreProgress } from '../RestoreProgress';
import { SaveProgress } from '../SaveProgress';
import { SignatureCertificationEditContractType } from './slides/SignatureCertificationEditContractType';
import { SignatureCertificationEditLanguage } from './slides/SignatureCertificationEditLanguage';
import { SignatureCertificationEditLegalClients } from './slides/SignatureCertificationEditLegalClients';
import { SignatureCertificationStart } from './slides/SignatureCertificationStart';
import { SignatureCertificationSummary } from './slides/SignatureCertificationSummary';
import { getOrCreateSignatureCertificationRelationship } from './utilities';

interface ISignatureCertificationDialogProps {
  configObject: IConfig;
}

type ISavedSignatureCertificationData =
  ISavedRelationshipDialogData<ISignatureCertificationRelationshipData>;

const SignatureCertificationDialog: React.FC<
  ISignatureCertificationDialogProps
> = ({ configObject }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [previousPage, setPreviousPage] = useState(-1);
  const [transitionStatus, setTransitionStatus] = useState<TransitionStatus>(
    TransitionStatus.IDLE
  );

  const [legalClientArray, putLegalClient] = useLegalClientStore(state => [
    state.legalClientArray,
    state.putLegalClient,
  ]);
  const [signatureCertificationData, setSignatureCertificationData] = useState(
    getEmptySignatureCertificationRelationshipData()
  );
  const [relationshipArray, setRelationshipArray] = useState(
    [] as ILegalTransactionRelationship[]
  );

  useEffect(() => {
    const relationship =
      getOrCreateSignatureCertificationRelationship(relationshipArray);

    if (relationship.data !== signatureCertificationData) {
      const updatedRelationship = {
        ...relationship,
        data: signatureCertificationData,
      };
      setRelationshipArray(
        updateOrCreateRelationship(relationshipArray, updatedRelationship)
      );
    }
  }, [signatureCertificationData, relationshipArray]);

  const handleBack = () => {
    setPreviousPage(currentPage);
    setCurrentPage(currentPage - 1);
    setTransitionStatus(TransitionStatus.EXITING);
  };
  const handleNext = () => {
    setPreviousPage(currentPage);
    setCurrentPage(currentPage + 1);
    setTransitionStatus(TransitionStatus.EXITING);
  };

  const onEntered = () => {
    setTransitionStatus(TransitionStatus.IDLE);
  };

  const onExited = () => {
    if (transitionStatus === TransitionStatus.EXITING) {
      setTransitionStatus(TransitionStatus.ENTERING);
    } else {
      setTransitionStatus(TransitionStatus.IDLE);
    }
  };

  const steps = [
    {
      name: 'Start',
      content: (
        <SignatureCertificationStart
          handleBack={handleBack}
          handleNext={handleNext}
        />
      ),
    },
    {
      name: 'Personen',
      content: (
        <SignatureCertificationEditLegalClients
          relationshipArray={relationshipArray}
          setRelationshipArray={setRelationshipArray}
          handleNext={handleNext}
          handleBack={handleBack}
        />
      ),
    },
    {
      name: 'Gegenstand',
      content: (
        <SignatureCertificationEditContractType
          scObject={signatureCertificationData}
          setScObject={setSignatureCertificationData}
          handleNext={handleNext}
          handleBack={handleBack}
        />
      ),
    },
    {
      name: 'Verwendung',
      content: (
        <SignatureCertificationEditLanguage
          scObject={signatureCertificationData}
          setScObject={setSignatureCertificationData}
          handleNext={handleNext}
          handleBack={handleBack}
        />
      ),
    },
    {
      name: 'Zusammenfassung',
      content: (
        <SignatureCertificationSummary
          configObject={configObject}
          signatureCertificationData={signatureCertificationData}
          setSignatureCertificationData={setSignatureCertificationData}
          relationshipArray={relationshipArray}
          setRelationshipArray={setRelationshipArray}
          handleNext={handleNext}
          handleBack={handleBack}
        />
      ),
    },
    {
      name: 'Abschluss',
      content: <Send />,
    },
  ];

  const restoreSetterFunction = (
    savedData: ISavedSignatureCertificationData
  ) => {
    const { relationshipArray, legalClientArray, data } = savedData;
    setRelationshipArray(relationshipArray);
    legalClientArray.forEach(putLegalClient);

    setSignatureCertificationData(data);
    setCurrentPage(0);
    setPreviousPage(-1);
    setTransitionStatus(TransitionStatus.IDLE);
  };
  const dataToSave = (): ISavedSignatureCertificationData => {
    return {
      relationshipArray,
      legalClientArray,
      data: signatureCertificationData,
    };
  };

  return (
    <DialogFrame
      configObject={configObject}
      title={'Formular zur Unterschriftsbeglaubigung'}
      currentPage={currentPage}
      stepperArray={createStepperArray({ steps, currentPage })}
      headerButtonArray={[
        <RestoreProgress
          key='signature-certification-import'
          setterFunction={restoreSetterFunction}
        ></RestoreProgress>,
        <SaveProgress
          key='signature-certification-export'
          idLegaltransaction={1}
          getDataToSave={dataToSave}
          filename={'Unterschriftsbeglaubigung'}
        ></SaveProgress>,
      ]}
      txt={''}
    >
      {createChildren({
        steps,
        currentPage,
        previousPage,
        transitionStatus,
        onEntered,
        onExited,
      })}
    </DialogFrame>
  );
};

export default SignatureCertificationDialog;
